import { useEffect, useRef, useState } from 'react';

import { hubConnection } from 'signalr-no-jquery';

import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { TokenService } from '../../app/he-thong/chung-thu-so/TokenService';
import { AppStorage } from '../../service/UserStorage';

import { translate, translateError } from '../language';
import { i18n, useTranslation } from '../language';

import { InfoDialogService } from '../../AppInfoDialog';
import { ConfirmDialogService } from '../../AppConfirmDialog';

import { LoadingService } from '../../AppLoading';

import { Service } from '../../service';
import EventEmitter from '../../utils/EventEmitter';
import {
  isPluginVersionSupported,
  PLUGIN_URL,
  PLUGIN_VERSION,
  requiresPluginVersionCheck,
} from '../../utils/pluginVersion';

interface SignOptions {
  // Chỉ truyền giá trị này khi nghiệp vụ ký yêu cầu kiểm tra version plugin.
  minimumPluginVersion?: string;
}

class SignService extends Service {

  static entity = 'app/sign-service';

  static sign(data: any): Promise<any> {
    return this.axios.post(`${this.entity}`, data).then(res => res.data);
  }

  static Invoice = 'Invoice';
  static Declare = 'Declare';
  static WrongInvoice = 'WrongInvoice';
  static InvoiceCode = 'InvoiceCode';
  static InvoiceSummary = 'Summary';
  static StatementInvoice = 'StatementInvoice';
  static StatementInvoiceBuyer = 'StatementInvoiceBuyer';


}

export const useSign = ({ onComplete }) => {
  const { t } = useTranslation();

  const [display, setDisplay] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [selectedToken] = useState(null);

  const [plugCommand, setPlugCommand] = useState(null);
  // Lưu riêng dữ liệu hóa đơn để kiểm tra nghiệp vụ, không gửi thêm vào lệnh ký của plugin.
  const invoiceContextRef = useRef<any>(null);

  useEffect(() => {
    if (plugCommand) {
      getTokens().then(tokens => {
        if (tokens && tokens.length) {
          if (tokens.length === 1) {
            signByToken(tokens[0]);
          } else {
            let defaultSign = 0, defaultToken;
            tokens.forEach(token => {
              if (token.defaultSign) {
                defaultSign++;
                defaultToken = token;
              }
            });
            if (defaultSign === 1) {
              signByToken(defaultToken);
            } else {
              setDisplay(true);
            }
          }
        } else {
          ConfirmDialogService.showInvalid(t('common.certificateNotConfigured'));
        }
      });
    }
  }, [plugCommand]); // eslint-disable-line react-hooks/exhaustive-deps

  const notificeEvent = () => {
    EventEmitter.emit("reloadSearchInvoiceSign");
  }

  const getTokens = () => {
    return new Promise<any>((resolve) => {
      if (tokens && tokens.length) {
        resolve(tokens);
      } else {
        TokenService.getList({ Used: true, MaxResultCount: 1000 }).then(({ items }) => {
          setTokens(items);
          resolve(items);
        }).catch(() => {
          resolve(tokens);
        });
      }
    });
  }

  // Nhận thêm ngữ cảnh hóa đơn vì PlugCommand thường chỉ còn danh sách ID.
  const sign = (PlugCommand?: any, invoiceContext?: any) => {
    invoiceContextRef.current =
      invoiceContext ?? PlugCommand?.InvoiceIdArray ?? null;
    setPlugCommand(PlugCommand);
  }

  const signByToken = (token) => {
    if (token.tokenType === 0) {
      plugCommand.SerialNumber = token.serialNumber;
      // Chỉ yêu cầu version mới khi ký USB hóa đơn điều chỉnh/thay thế nhiều.
      const minimumPluginVersion =
        plugCommand.CommandName === "SignInvoices" &&
        requiresPluginVersionCheck(invoiceContextRef.current)
          ? PLUGIN_VERSION
          : undefined;

      Sign(plugCommand, { minimumPluginVersion })
        .then(result => {
          if (onComplete) {
            onComplete(result);
            notificeEvent();
          }
        })
        .catch(() => undefined);
    } else {
      let signType, ids;
      switch (plugCommand.CommandName) {
        case "SignInvoices":
          signType = SignService.Invoice;
          ids = plugCommand.InvoiceIdArray;
          break;
        case "SignSummary":
          signType = SignService.InvoiceSummary;
          ids = plugCommand.InvoiceIdArray;
          break;
        case "SignDeclaration":
          signType = SignService.Declare;
          ids = [plugCommand.Id];
          break;
        case "SignSS04":
          signType = SignService.WrongInvoice;
          ids = [plugCommand.Id];
          break;
        case "StatementInvoice":
          signType = SignService.StatementInvoice;
          ids = plugCommand.InvoiceIdArray;
          break;
        case "StatementInvoiceBuyer":
          signType = SignService.StatementInvoiceBuyer;
          ids = plugCommand.InvoiceIdArray;
          break;
      }
      signService({ signType, ids, tokenInfo: token });
    }
  }
  const signService = (singInfo) => {
    SignService.sign(singInfo).then(result => {
      if (onComplete) {
        onComplete(result);
        notificeEvent();
      }
    }).catch(({ response }) => {
      ConfirmDialogService.showInvalid(translateError(response));
    });
  }
  const [isSigning, setIsSigning] = useState(false);
  const isSigningRef = useRef(false);

  useEffect(() => {
    if (display) {
      setIsSigning(false);
      isSigningRef.current = false;
    }
  }, [display]);

  const onSelectionChange = (e) => {
    if (isSigningRef.current || !e.value) {
      return;
    }
    isSigningRef.current = true;
    setIsSigning(true);
    signByToken(e.value);
    setDisplay(false);
  };

  const renderSign = () => {
    return (
      <Dialog
        header={t('common.selectCertificate')}
        visible={display}
        style={{ width: '60rem' }}
        position="top"
        onHide={() => {
          if (!isSigning) {
            setDisplay(false);
          }
        }}
      >
        <DataTable
          value={tokens}
          selectionMode={isSigning ? null : "single"}
          selection={selectedToken}
          onSelectionChange={onSelectionChange}
          dataKey="id"
          responsiveLayout="scroll"
          className="certificate-select-table"
          rowHover={!isSigning}
          style={{
            pointerEvents: isSigning ? 'none' : 'auto',
            opacity: isSigning ? 0.6 : 1,
            cursor: isSigning ? 'not-allowed' : 'pointer'
          }}
        >
          <Column field="vender" header={t('common.certificateProvider')}></Column>
          <Column field="subjectName" header={t('common.certificateOwner')}></Column>
          <Column field="tokenType" header={t('common.certificateType')} body={rowData => TokenService.tokenTypes[rowData.tokenType].label}></Column>
        </DataTable>
      </Dialog>
    );
  }

  return { renderSign, sign };
}

// Thông báo chặn ký và cung cấp link tải plugin khi version chưa đáp ứng.
const showPluginUpdateDialog = () => {
  InfoDialogService.show({
    header: i18n.t('common.pluginUpdate'),
    width: '50rem',
    body: (
      <div>
        <p>{i18n.t('common.pluginVersionRequirement', { version: PLUGIN_VERSION })}</p>
        <a
          href={PLUGIN_URL}
          rel="noreferrer"
          target="_blank"
          className="text-lg"
        >
          <i className="pi pi-download mr-2"></i>
          {i18n.t('common.pluginDownload')}
        </a>
      </div>
    )
  });
};

export const Sign = (PlugCommand: any, options: SignOptions = {}) => {

  LoadingService.start();

  let connectionHub;
  return new Promise<any>((resolve, reject) => {
    // Tách cấu hình kết nối hub khỏi SignOptions dùng cho kiểm tra version.
    const connectionOptions: any = {
      'Content-Type': 'application/json',
      'Accept': '*/*',
      qs: {
        'Access-Control-Allow-Private-Network': true
      }
    };
    connectionHub = hubConnection('http://localhost:16080', connectionOptions);

    const hubProxy = connectionHub.createHubProxy('handlerHub');
    hubProxy.on('reponseResult', (resultText) => {
      const result = JSON.parse(resultText);
      if (result.code === 1) {
        resolve(result);
      } else {
        reject();
        ConfirmDialogService.showInvalid(translate(`ErrorCode-${result.code}`, result.message));
      }
    });

    const data = {
      "BrowserInfo": {
        "Cookie": document.cookie,
        "UserAgent": navigator.userAgent,
        "UserName": AppStorage.get('UserName'),
        "Pass": AppStorage.get('Pass'),
        "LinkApi": process.env.REACT_APP_API_SIGN
      },
      "PlugCommand": PlugCommand
    };

    connectionHub.start().done(() => {
      // Chỉ gửi lệnh ký sau khi plugin đã vượt qua bước kiểm tra version (nếu có).
      const processMessage = () => {
        hubProxy.invoke('processMessage', JSON.stringify(data)).then();
      };

      // Các nghiệp vụ ký thông thường giữ nguyên luồng cũ, không gọi getVersion.
      if (!options.minimumPluginVersion) {
        processMessage();
        return;
      }

      // Plugin version thấp hoặc không hỗ trợ getVersion đều phải cập nhật trước khi ký lại.
      const rejectForUnsupportedVersion = () => {
        showPluginUpdateDialog();
        reject();
      };

      try {
        // Lấy version trực tiếp từ M-INVOICE Plugin qua handlerHub.
        hubProxy.invoke('getVersion').then(
          (version) => {
            // Version hợp lệ thì mới tiếp tục gửi processMessage để ký.
            if (
              isPluginVersionSupported(
                version,
                options.minimumPluginVersion,
              )
            ) {
              processMessage();
              return;
            }

            rejectForUnsupportedVersion();
          },
          rejectForUnsupportedVersion,
        );
      } catch {
        rejectForUnsupportedVersion();
      }
    }).fail(() => {
      InfoDialogService.show({
        header: i18n.t('report.pluginSignFailed'),
        width: '50rem',
        body: (
          <div className="grid">
            <div className="col-2 text-center">
              <img src="/images/usb-not-connected-icon.png" alt="" style={{ width: '80%' }} />
            </div>
            <div className="col-10">
              <p>{i18n.t('report.pluginCheckSteps')}</p>
              <p><b>1.</b> {i18n.t('report.pluginNotInstalled')}</p>
              <p className="pl-4">{i18n.t('report.pluginInstallToContinue')}</p>
              <p className="pl-4">
                <a href={PLUGIN_URL} rel="noreferrer" target="_blank" className="text-lg mr-5"><i className="pi pi-download mr-2"></i>{i18n.t('report.downloadInstaller')}</a>
                <a href={'https://www.youtube.com/playlist?list=PLpmokHjV2pDpdSxt4wai1ZX6-dR8EKp21'} rel="noreferrer" target="_blank" className="text-lg"><i className="pi pi-info-circle mr-2"></i>{i18n.t('report.installGuide')}</a>
              </p>
              <p><b>2.</b> {i18n.t('report.pluginInstalled')}</p>
              <p className="pl-4">{i18n.t('report.openPluginBeforeSigning')}</p>
              <p className="pl-4">
                <a href={`minvoice:${AppStorage.get('tenant')}`} className="text-lg"><i className="pi pi-info-circle mr-2"></i>{i18n.t('report.openPlugin')}</a>
              </p>
            </div>
          </div>
        )
      });
      reject();
    });
  }).finally(() => {
    if (connectionHub) {
      connectionHub.stop();
    }
    LoadingService.stop();
  })
}
